import React from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  Comment,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import useStyles from '../styles';
import { ProgramCardType } from '../types';
import CardPrice from './CardPrice';
import { formatDateRange, formatSchedule, formatSectionLocation } from '../../../utils/formatters';
import { ReduxState } from '../../../types';
import { ADMINISTRATOR, SECRETARY, STUDENT } from '../../../utils/user_types';

function ProgramCard({
  studentProgram,
  showPrices,
  onAssignSection,
  onEditProgram,
  onDeleteProgram,
}: ProgramCardType) {
  const classes = useStyles();
  const account = useSelector((state: ReduxState) => state.account);

  // Los handlers llegan undefined cuando el plan ya fue aceptado: la pestaña queda informativa.
  const canManagePrograms = (account?.user?.userType === ADMINISTRATOR
    || account?.user?.userType === SECRETARY);
  const canEditProgram = canManagePrograms && Boolean(onEditProgram) && Boolean(onDeleteProgram);
  const canAssignSection = canManagePrograms && Boolean(onAssignSection);

  const isStudent = account?.user?.userType === STUDENT;

  const handleEditProgram = () => {
    if (onEditProgram) {
      onEditProgram(studentProgram);
    }
  };

  const handleDeleteProgram = () => {
    if (onDeleteProgram) {
      onDeleteProgram(studentProgram);
    }
  };

  const handleAssignSection = () => {
    if (onAssignSection) {
      onAssignSection(studentProgram);
    }
  };

  return (
    <Box className={isStudent ? classes.studentCard : classes.card} key={studentProgram.id}>
      <Box className={classes.cardTitleSection}>
        <Box>
          <Typography className={classes.cardTitle}>
            {studentProgram.program.subject.name}
          </Typography>
          <Typography className={classes.cardSubtitle}>
            {studentProgram.program.name}
          </Typography>
        </Box>
        {canEditProgram && (
          <Box className={classes.cardButtonsContainer}>
            <Button
              variant="contained"
              color="primary"
              className={classes.cardButton}
              onClick={handleEditProgram}
            >
              Editar
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.cardButton}
              onClick={handleDeleteProgram}
            >
              Eliminar
            </Button>
          </Box>
        )}
      </Box>
    <Divider />
    <Box className={classes.innerSection}>
      <CalendarToday className={classes.innerSectionIcon} />
      <Box>
        <Typography className={classes.innerSectionTitle}>
          Rango de fechas:
        </Typography>
        <Typography className={classes.innerSectionText}>
          {formatDateRange(
            studentProgram.program.startDate,
            studentProgram.program.endDate,
          )}
        </Typography>
      </Box>
    </Box>
    <Box className={classes.innerSection}>
      <AccessTime className={classes.innerSectionIcon} />
      <Box>
        <Typography className={classes.innerSectionTitle}>Horarios:</Typography>
        <Typography className={classes.innerSectionText}>
          {formatSchedule(studentProgram.section)}
        </Typography>
      </Box>
    </Box>
    <Box className={classes.innerSection}>
      <LocationOn className={classes.innerSectionIcon} />
      <Box>
        <Typography className={classes.innerSectionTitle}>Sede y Sala:</Typography>
        <Typography className={classes.innerSectionText}>
          {formatSectionLocation(studentProgram.section)}
        </Typography>
      </Box>
    </Box>
    {studentProgram.comment && (
      <Box className={classes.innerSection}>
        <Comment className={classes.innerSectionIcon} />
        <Box>
          <Typography className={classes.innerSectionTitle}>
            Comentarios:
          </Typography>
          <Typography className={classes.innerSectionText}>
            {studentProgram.comment || '-'}
          </Typography>
        </Box>
      </Box>
    )}
    {showPrices && (
      <Box className={classes.assignSection}>
        {!isStudent && (
          <CardPrice
            title="Arancel Final"
            originalPrice={studentProgram.program.fee}
            discountPrice={studentProgram.feeDiscount}
            finalPrice={studentProgram.program.fee - studentProgram.feeDiscount} />
        )}
        {canAssignSection && (
          <Button
            variant="contained"
            color="primary"
            className={classes.cardButton}
            onClick={handleAssignSection}
          >
            {studentProgram.section ? 'Editar Sección' : 'Asignar Sección'}
          </Button>
        )}
      </Box>
    )}
  </Box>
  );
}

export default ProgramCard;
